import type {
  MarginInventoryChange,
  MarginInventoryLatestEntry,
  MarginMarketInfo,
} from "@yutai/shared"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { MarginCrossCost } from "./MarginCrossCost.js"
import { MarginInventoryHistory } from "./MarginInventoryHistory.js"
import styles from "./MarginInventoryRowCard.module.css"
import { type BenefitValueOverrides, benefitOptions } from "./benefitOptions.js"
import { describeChange } from "./inventoryHistory.js"
import type { ThisMonthInventoryRow } from "./thisMonthInventory.js"

function ChangeItem({ change }: { change: MarginInventoryChange }) {
  const { direction, amount, percent } = describeChange(change)
  const [, m, d] = change.date.split("-")
  return (
    <li className={styles.change}>
      <span className={styles.changeDate}>
        {Number(m)}/{Number(d)}
      </span>
      <span className={direction === "up" ? styles.up : styles.down}>
        {direction === "up" ? "↑" : "↓"}
        {amount.toLocaleString("ja-JP")}
        {percent !== null && ` (${percent}%)`}
      </span>
    </li>
  )
}

function Quantity({
  label,
  entry,
  onOpen,
  open,
}: {
  label: string
  entry: MarginInventoryLatestEntry | undefined
  onOpen: () => void
  open: boolean
}) {
  const q = entry?.quantity
  return (
    <div className={styles.qty}>
      <div className={styles.qtyMain}>
        <span className={styles.qtyLabel}>{label}</span>
        <button
          type="button"
          className={`${styles.qtyButton} ${q ? styles.inStock : styles.noStock}`}
          onClick={onOpen}
          aria-expanded={open}
          title="在庫の履歴を表示"
        >
          {q == null ? "—" : q.toLocaleString("ja-JP")}
        </button>
      </div>
      {entry && entry.changes.length > 0 && (
        <ul className={styles.changes}>
          {entry.changes.map((c) => (
            <ChangeItem key={c.date} change={c} />
          ))}
        </ul>
      )}
    </div>
  )
}

interface Props {
  row: ThisMonthInventoryRow
  market: MarginMarketInfo | undefined
  today: string
  loanRate: number
  overrides: BenefitValueOverrides | undefined
  onSaveOverrides: (values: BenefitValueOverrides) => Promise<void>
  onCrawl: () => Promise<void>
  memo: string
  onSaveMemo: (memo: string) => Promise<void>
}

// 一般信用在庫ページの1銘柄分: 在庫（14日・無期限）、優待内容、管理者用メモ。
export function MarginInventoryRowCard({
  row,
  market,
  today,
  loanRate,
  overrides,
  onSaveOverrides,
  onCrawl,
  memo,
  onSaveMemo,
}: Props) {
  const { benefit } = row
  const [draft, setDraft] = useState(memo)
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle")
  const [expanded, setExpanded] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  // 優待の段階（株数）。買い必要額と優待クロス費用の両方に反映する
  const options = benefitOptions(benefit, overrides)
  const [selectedShares, setSelectedShares] = useState<number | null>(null)
  const option = options.find((o) => o.shares === selectedShares) ?? options[0]

  useEffect(() => setDraft(memo), [memo])

  useEffect(() => {
    if (status !== "saved") return
    const timer = window.setTimeout(() => setStatus("idle"), 2000)
    return () => window.clearTimeout(timer)
  }, [status])

  async function save() {
    if (draft === memo || status === "saving") return
    setStatus("saving")
    try {
      await onSaveMemo(draft)
      setStatus("saved")
    } catch (e) {
      console.error(e)
      setStatus("error")
    }
  }

  const title = benefit.title || benefit.description
  const description = benefit.title ? benefit.description : ""

  return (
    <li className={styles.card}>
      <div className={styles.head}>
        <div className={styles.company}>
          <span className={styles.code}>{row.code}</span>
          <Link to={`/companies/${row.code}`} className={styles.name}>
            {row.name}
          </Link>
        </div>
        <div className={styles.qtys}>
          <Quantity
            label="14日"
            entry={row.inventory["14d"]}
            open={showHistory}
            onOpen={() => setShowHistory((v) => !v)}
          />
          <Quantity
            label="無期限"
            entry={row.inventory.unlimited}
            open={showHistory}
            onOpen={() => setShowHistory((v) => !v)}
          />
        </div>
      </div>

      {showHistory && <MarginInventoryHistory code={row.code} />}

      <div className={styles.benefit}>
        {options.length > 1 && (
          <div className={styles.tiers}>
            {options.map((o) => (
              <button
                key={o.shares}
                type="button"
                className={`${styles.tier} ${o === option ? styles.tierOn : ""}`}
                aria-pressed={o === option}
                onClick={() => setSelectedShares(o.shares)}
              >
                {o.shares.toLocaleString("ja-JP")}株{o.overridden && "✎"}
                {o.value !== null && ` ${o.value.toLocaleString("ja-JP")}円`}
              </button>
            ))}
          </div>
        )}
        <p className={styles.benefitMeta}>
          {option.shares.toLocaleString("ja-JP")}株〜
          {option.value !== null && ` ・ ${option.value.toLocaleString("ja-JP")}円相当`}
        </p>
        {market && market.price > 0 && (
          <p className={styles.buyCost}>
            買い必要額 {Math.round(market.price * option.shares).toLocaleString("ja-JP")}円
          </p>
        )}
        {title && <p className={styles.benefitTitle}>{title}</p>}
        {description && (
          <button
            type="button"
            className={`${styles.benefitDesc} ${expanded ? styles.benefitDescOpen : ""}`}
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {description}
          </button>
        )}
      </div>

      {market && (
        <MarginCrossCost
          options={options}
          option={option}
          market={market}
          inventory={row.inventory}
          today={today}
          loanRate={loanRate}
          overrides={overrides}
          onSaveOverrides={onSaveOverrides}
          onCrawl={onCrawl}
        />
      )}

      <label className={styles.memo}>
        <span className={styles.memoLabel}>
          メモ
          {status === "saving" && <span className={styles.memoStatus}>保存中...</span>}
          {status === "saved" && <span className={styles.memoStatus}>✓ 保存しました</span>}
          {status === "error" && <span className={styles.memoError}>保存に失敗しました</span>}
        </span>
        <textarea
          className={styles.memoInput}
          value={draft}
          rows={draft ? 2 : 1}
          placeholder="クロス予定・注意点など"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
        />
      </label>
    </li>
  )
}
